import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import {
    ContestDiscount,
    DiscountCodeBase,
    ICreateDiscountCode,
    IManagedEventDiscountCode,
    TCodeType,
} from '../../../../redux/events/types';
import { CustomDatePicker } from '../../../DatePicker/CustomDatePicker';

export interface CreateDiscountDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (discount: ICreateDiscountCode) => void;
    onUpdate?: (id: string, discount: ICreateDiscountCode) => void;
    onDelete?: (id: string) => void;
    eventId?: string;
    editDiscount?: IManagedEventDiscountCode;
    contestId?: string;
    codeType?: TCodeType;
    readOnlyCodeType?: boolean;
}

export const CreateDiscountDialog: React.FC<CreateDiscountDialogProps> = ({
    open,
    onClose,
    onSubmit,
    onUpdate,
    onDelete,
    eventId,
    editDiscount,
    contestId,
    codeType = 'discount',
    readOnlyCodeType,
}) => {
    const { themeStyle } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const [discountData, setDiscountData] = useState<Partial<ICreateDiscountCode>>({
        code_type: codeType,
        discount_type: 'percentage',
        discount: 0,
        active: true,
        event: eventId,
        discount_structure: 'one_time',
        expiration_date: '',
    });

    useEffect(() => {
        if (eventId) {
            setDiscountData(prevState => ({
                ...prevState,
                event: eventId,
                contest: undefined,
                audition: undefined,
                code_type: 'discount',
            }));
        }

        if (contestId) {
            setDiscountData(prevState => ({
                ...prevState,
                event: undefined,
                contest: contestId,
                audition: undefined,
                code_type: 'discount',
            }));
        }
    }, [eventId, contestId]);

    useEffect(() => {
        if (editDiscount) {
            setDiscountData({
                code_type: editDiscount.code_type as 'discount' | 'coupon' | 'reedemable',
                discount_type: editDiscount.discount_type as 'percentage' | 'fixed',
                discount: editDiscount.discount,
                active: editDiscount.active,
                event: editDiscount.event,
                discount_structure: editDiscount.discount_structure as 'one_time' | 'time_period',
                expiration_date: editDiscount.expiration_date,
            });
        }
    }, [editDiscount]);

    const handleInputChange = (field: keyof ICreateDiscountCode, value: any) => {
        setDiscountData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = () => {
        if (editDiscount && onUpdate) {
            onUpdate(editDiscount._id, discountData as ICreateDiscountCode);
        } else {
            if ((contestId && discountData.code_type === 'reedemable') || discountData.code_type === 'discount') {
                const payload: ContestDiscount = {
                    ...(discountData as DiscountCodeBase),
                    contest: contestId,
                    code_type: discountData.code_type,
                    event: undefined,
                    audition: undefined,
                };

                onSubmit(payload);
            }
        }
        setDiscountData({});
        onClose();
    };

    const handleDelete = () => {
        if (editDiscount && onDelete) {
            onDelete(editDiscount._id);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    backgroundColor: theme[themeStyle].pure_black,
                    borderRadius: { xs: 0, sm: 2 },
                    minWidth: { xs: '100%', sm: '500px' },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    overflowY: 'visible',
                },
            }}>
            <DialogTitle
                sx={{
                    color: theme[themeStyle].primary_white,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    borderBottom: `1px solid ${theme[themeStyle].placeholderGray}`,
                    pb: 2,
                }}>
                {editDiscount ? 'Edit Discount' : 'Create New Discount'}
            </DialogTitle>
            <DialogContent
                sx={{
                    pt: 3,
                    mt: 2,
                    '&.MuiDialogContent-root': {
                        paddingTop: '16px',
                    },
                }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].light_gray_text }}>Code Type</InputLabel>
                            <Select
                                value={discountData.code_type || 'discount'}
                                disabled={readOnlyCodeType}
                                onChange={e => handleInputChange('code_type', e.target.value)}
                                label="Code Type"
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <MenuItem value="discount">Discount</MenuItem>
                                <MenuItem value="coupon">Coupon</MenuItem>
                                <MenuItem value="reedemable">Redeemable</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].light_gray_text }}>Discount Type</InputLabel>
                            <Select
                                value={discountData.discount_type || 'percentage'}
                                onChange={e => handleInputChange('discount_type', e.target.value)}
                                label="Discount Type"
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <MenuItem value="percentage">Percentage (%)</MenuItem>
                                <MenuItem value="fixed">Fixed Amount ($)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Discount Value"
                            type="number"
                            value={discountData.discount || ''}
                            onChange={e => handleInputChange('discount', parseFloat(e.target.value))}
                            fullWidth
                            InputLabelProps={{
                                sx: { color: theme[themeStyle].light_gray_text },
                            }}
                            InputProps={{
                                sx: {
                                    color: theme[themeStyle].primary_white,
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme[themeStyle].light_gray_text }}>
                                Discount Structure
                            </InputLabel>
                            <Select
                                value={discountData.discount_structure || 'one_time'}
                                onChange={e => handleInputChange('discount_structure', e.target.value)}
                                label="Discount Structure"
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    backgroundColor: theme[themeStyle].secondaryBg,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].placeholderGray50,
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: theme[themeStyle].primary_white,
                                    },
                                }}>
                                <MenuItem value="one_time">One Time</MenuItem>
                                <MenuItem value="time_period">Time Period</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].light_gray_text,
                                mb: 1,
                            }}>
                            Expiration Date
                        </Typography>
                        <CustomDatePicker
                            value={discountData.expiration_date}
                            onChange={date => handleInputChange('expiration_date', date)}
                            minDate={new Date()}
                            withTime
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 2,
                    borderTop: `1px solid ${theme[themeStyle].placeholderGray}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                }}>
                <div>
                    {editDiscount && onDelete && (
                        <Button
                            onClick={handleDelete}
                            sx={{
                                backgroundColor: theme[themeStyle].primary_red,
                                color: theme[themeStyle].primary_white,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                '&:hover': {
                                    backgroundColor: theme[themeStyle].primary_red,
                                    opacity: 0.9,
                                },
                                mr: 1,
                            }}>
                            Delete
                        </Button>
                    )}
                    <Button
                        onClick={onClose}
                        sx={{
                            color: theme[themeStyle].primary_white,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        Cancel
                    </Button>
                </div>
                <Button
                    onClick={handleSubmit}
                    disabled={!discountData.discount || !discountData.expiration_date}
                    sx={{
                        backgroundColor: theme[themeStyle].highlight,
                        color: theme[themeStyle].black,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].primary_orange,
                            opacity: 0.9,
                        },
                        '&:disabled': {
                            backgroundColor: theme[themeStyle].placeholderGray,
                            color: theme[themeStyle].light_gray_text,
                        },
                        px: 2,
                    }}>
                    {editDiscount ? 'Update Discount' : 'Create Discount'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
